/* eslint-disable max-len */
import {
  OperationPart,
  LaborTime as OperationLaborTime,
} from '@1po/1po-bff-fe-spec/generated/catalog/easy_basket/response/GetEasyBasketOperationDetailResponse';
import { IAMLaborTime } from '@1po/1po-bff-fe-spec/generated/catalog/labor_time/response/GetIAMLaborTimesResponse';
import { LaborTime as DHLaborTime } from '@1po/1po-bff-fe-spec/generated/catalog/labor_time/response/GetLaborTimesResponse';
import { LinkedReference } from '@1po/1po-bff-fe-spec/generated/catalog/references/dh/model/Reference';
import { VehicleDetail as CatalogVehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import {
  AddOperationFromEasyBasket,
  Reference as EasyBasketReference,
  LaborTime as EasyBasketLaborTime,
  LaborTimeDuration,
  VehicleDetail,
} from '@1po/1po-bff-fe-spec/generated/estimate/request/AddOperationFromEasyBasket';
import { LaborTime } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/LaborTime';
import {
  CatalogSource,
  Origin,
  ReferenceBrandType,
  ReferenceSource,
} from '@1po/1po-bff-fe-spec/generated/estimate/request/model/Reference';
import { Tire } from '@1po/1po-bff-fe-spec/generated/estimate/request/model/Tire';
import { Vehicle as EstimateVehicleModel } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { Settings } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetSettings';
import { v4 as uuidv4 } from 'uuid';
import { EstimateLocal, RequestReference } from 'domains/estimate/Estimate.types';
import { ReferencePriceType } from 'domains/references';
import {
  getLaborDetailList,
  getLaborTimeRate,
  getTechnicityLevel,
  MECHANICS,
} from 'pages/CatalogPage/DH/SubcategorySection/LaborTimeSection/utils';
import { NO_DATA, getData } from 'utils';

/**
 * Maps an estimate Vehicle to catalog VehicleDetail for switch/select hydration.
 * Estimate vehicles omit `vehicleIdentifiedBy`; derive it from vin, vrn, or vehicleKey.
 *
 * @param vehicle - Estimate or history vehicle payload
 * @returns Catalog VehicleDetail suitable for `/vehicles/select`
 * @example
 * fetchVehicleFunction(dispatch, key, false, country, source, undefined, version, toCatalogVehicleDetail(vehicle), uuidv4());
 */
export const toCatalogVehicleDetail = (vehicle: EstimateVehicleModel): CatalogVehicleDetail => ({
  catalogSource: vehicle.catalogSource,
  vrn: vehicle.vrn,
  vin: vehicle.vin,
  vehicleKey: vehicle.vehicleKey,
  model: vehicle.model,
  imageUrl: vehicle.imageUrl,
  vehicleBrand: vehicle.vehicleBrand,
  country: vehicle.country,
  manufacturingDate: vehicle.manufacturingDate,
  dataHubVehicle: vehicle.dataHubVehicle,
  iamVehicle: vehicle.iamVehicle,
  vehicleIdentifiedBy: vehicle.vin ?? vehicle.vrn ?? vehicle.vehicleKey,
});

export const convertDHLaborTimeToEstimateLaborTime = (
  laborTimeLocal: DHLaborTime,
  estimateSettings: Settings,
): LaborTime => {
  return convertDHLaborTimeToEstimateLaborTimeWithQuantity(laborTimeLocal, 1, estimateSettings);
};

export const convertIAMLaborTimeToEstimateLaborTime = (
  laborTimeLocal: IAMLaborTime,
  estimateSettings: Settings,
): LaborTime => {
  return convertIAMLaborTimeToEstimateLaborTimeWithQuantity(laborTimeLocal, 1, estimateSettings);
};

const convertDHLaborTimeToEstimateLaborTimeWithQuantity = (
  laborTimeLocal: DHLaborTime,
  quantity: number,
  estimateSettings: Settings,
): LaborTime => {
  const laborDetailList = getLaborDetailList(
    laborTimeLocal.time,
    laborTimeLocal.technicity,
    estimateSettings.laborPriceList,
  );

  const priceVATExcl = laborDetailList.reduce((total, laborDetail) => {
    if (!laborDetail.laborTimeRate) {
      return total;
    }
    const laborTimeRate = Number.parseFloat(laborDetail.laborTimeRate);
    return Number.isNaN(laborTimeRate) ? total : total + laborTimeRate * laborDetail.time;
  }, 0);

  const estimateRate =
    priceVATExcl && laborTimeLocal.time.totalHours > 0 ? priceVATExcl / laborTimeLocal.time.totalHours : undefined;

  return {
    catalogSource: 'DATAHUB',
    code: laborTimeLocal.code,
    designation: laborTimeLocal.designation.title ?? '',
    pricePerHour: estimateRate?.toString() ?? '',
    numberOfHours: laborTimeLocal.time.totalHours,
    quantity,
    priceVatExcluded: priceVATExcl.toString(),
    vatPercentage: estimateSettings?.vatRate,
    includedOperations: getOperationsDesignationsFromLaborTimes(laborTimeLocal.subLaborTimes?.includedLaborTimes ?? []),
    notIncludedOperations: getOperationsDesignationsFromLaborTimes(
      laborTimeLocal.subLaborTimes?.notIncludedLaborTimes ?? [],
    ),
    details: laborTimeLocal.details,
    technicity: laborTimeLocal.technicity,
    time: laborTimeLocal.time,
  };
};

const convertIAMLaborTimeToEstimateLaborTimeWithQuantity = (
  laborTimeLocal: IAMLaborTime,
  quantity: number,
  estimateSettings: Settings,
): LaborTime => {
  const estimateRate = getLaborTimeRate(MECHANICS, laborTimeLocal.technicity, estimateSettings.laborPriceList)?.price;
  const estimateRateNumber = estimateRate ? Number(estimateRate) : 1;
  return {
    catalogSource: 'IAM',
    code: laborTimeLocal.id,
    designation: laborTimeLocal.designation ?? '',
    pricePerHour: estimateRate ?? '',
    numberOfHours: laborTimeLocal.time,
    quantity,
    priceVatExcluded: String(laborTimeLocal.time * estimateRateNumber),
    vatPercentage: estimateSettings?.vatRate,
    includedOperations: getOperationsDesignationsFromIAMLaborTimes(laborTimeLocal.includedLaborTimes ?? []),
    notIncludedOperations: getOperationsDesignationsFromIAMLaborTimes(laborTimeLocal.optionalLaborTimes ?? []),
    technicity: laborTimeLocal.technicity,
    technicityType: 'MECHANICS',
  };
};

export const convertCatalogTestLaborTimesToEstimateLaborTimes = (
  tests: DHLaborTime[] | undefined,
  testLaborTimeQuantities: Map<string, number>,
  estimateSettings: Settings,
): LaborTime[] => {
  if (!tests) return [];
  return tests
    .filter((test) => testLaborTimeQuantities.get(test.code))
    .map((test) => {
      return convertDHLaborTimeToEstimateLaborTimeWithQuantity(
        test,
        testLaborTimeQuantities.get(test.code) ?? 1,
        estimateSettings,
      );
    })
    .filter((x) => x);
};

export const convertReferenceToEstimateReference = (
  referenceNumber: string,
  designation: string,
  price: ReferencePriceType,
  isCompatible: boolean,
  catalogSource: CatalogSource,
  brand: ReferenceBrandType | undefined,
  origin?: Origin,
  supplierCode?: string,
  referenceSource?: ReferenceSource,
  eprelUrl?: string,
  linkedReferences?: LinkedReference[],
  commercialFamily?: string,
  imageUrl?: string,
  quantity?: number,
  sellerNumber?: string,
  sellerName?: string,
): RequestReference => {
  return {
    referenceNumber,
    designation,
    catalogSource,
    isCompatible,
    eprelUrl,
    origin,
    supplierCode,
    referenceSource,
    linkedReferences,
    commercialFamily,
    brand,
    imageUrl,
    unitPrice: price.clientView?.recommendedPriceVatExcluded?.toString() ?? '',
    quantity: quantity ?? 1,
    priceVatExcluded: price.clientView?.recommendedPriceVatExcluded?.toString() ?? '',
    vatPercentage: price.garageView?.vatPercentage ?? 20,
    garagePrice: price.garageView?.vatExcludedPrice?.toString() ?? '',
    discountRate: price.garageView?.discountRate ?? '',
    sellerNumber,
    sellerName,
  };
};

export const convertTireToEstimateTire = (
  referenceNumber: string,
  designation: string,
  price: ReferencePriceType,
  isCompatible: boolean,
  quantity: number,
  diameter: string,
  brandId?: string,
  eprelUrl?: string,
  tireDimensions?: string,
  commercialFamily?: string,
): Tire => {
  return {
    catalogSource: 'DATAHUB',
    unitPrice: price.clientView?.recommendedPriceVatExcluded?.toString() ?? '',
    priceVatExcluded: price.clientView?.recommendedPriceVatExcluded?.toString() ?? '',
    vatPercentage: price.garageView?.vatPercentage ?? 20,
    garagePrice: price.garageView?.vatExcludedPrice?.toString() ?? '',
    discountRate: price.garageView?.discountRate ?? '',
    referenceNumber,
    designation,
    quantity,
    diameter,
    brandId,
    isCompatible,
    eprelUrl,
    tireDimensions,
    commercialFamily,
  };
};

const getOperationsDesignationsFromLaborTimes = (laborTimes: DHLaborTime[]): string[] => {
  return laborTimes.map((labor) => {
    if (!labor.designation || !labor.designation.title) {
      return labor.code ?? '';
    }
    if (!labor.code) {
      return labor.designation.title ?? '';
    }
    return `${labor.code} - ${labor.designation.title}`;
  });
};

const parseDecimal = (value: string | undefined): number => {
  if (!value) return 0;
  const normalized = value.replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getOperationsDesignationsFromIAMLaborTimes = (laborTimes: IAMLaborTime[]): string[] => {
  return laborTimes.map((labor) => `${labor.id} - ${labor.designation}`);
};

const convertEasyBasketLaborTimeToEstimateLaborTimeWithQuantity = (
  laborTime: OperationLaborTime,
  quantity: number,
  estimateSettings: Settings,
): EasyBasketLaborTime => {
  const businessSpecificities = laborTime.businessSpecificities;

  const mechanics = parseDecimal(businessSpecificities?.mechanics);
  const trim = parseDecimal(businessSpecificities?.trim);
  const electricity = parseDecimal(businessSpecificities?.electricity);
  const panelwork = parseDecimal(businessSpecificities?.panelwork);
  const painting = parseDecimal(businessSpecificities?.painting);

  const totalHours = mechanics + trim + electricity + panelwork + painting;

  const time: LaborTimeDuration = {
    totalHours,
    mechanicsHours: mechanics > 0 ? mechanics : undefined,
    trimHours: trim > 0 ? trim : undefined,
    electricityHours: electricity > 0 ? electricity : undefined,
    panelWorkHours: panelwork > 0 ? panelwork : undefined,
    paintingHours: painting > 0 ? painting : undefined,
  };

  const laborDetailList = getLaborDetailList(
    time,
    getTechnicityLevel(laborTime.technicality),
    estimateSettings.laborPriceList,
  );

  const priceVATExcl = laborDetailList.reduce((total, laborDetail) => {
    if (!laborDetail.laborTimeRate) {
      return total;
    }
    const laborTimeRate = Number.parseFloat(laborDetail.laborTimeRate);
    return Number.isNaN(laborTimeRate) ? total : total + laborTimeRate * laborDetail.time;
  }, 0);

  const estimateRate = priceVATExcl && totalHours > 0 ? priceVATExcl / totalHours : undefined;

  return {
    catalogSource: 'DATAHUB',
    code: laborTime.id,
    designation: laborTime.designation ?? '',
    pricePerHour: estimateRate?.toString() ?? '',
    numberOfHours: totalHours,
    quantity,
    priceVatExcluded: priceVATExcl.toString(),
    vatPercentage: estimateSettings?.vatRate,
    time,
    details: laborTime.characteristics
      ? {
          precision: laborTime.characteristics.elementPrecision,
          additionalLabel: laborTime.characteristics.additionalLabel,
          quantity: laborTime.characteristics.quantity,
          paintType: laborTime.characteristics.paintType,
          paintClass: laborTime.characteristics.paintClass,
        }
      : undefined,
    technicity: getTechnicityLevel(laborTime.technicality),
    technicityType: 'MECHANICS',
    includedOperations: laborTime.detail?.included?.map((d) => `${d.operation} - ${d.designation}`) ?? [],
    notIncludedOperations: laborTime.detail?.notIncluded?.map((d) => `${d.operation} - ${d.designation}`) ?? [],
  };
};

export const getOperationEstimateRequestData = (
  operationName: string,
  references: OperationPart[],
  laborTimes: OperationLaborTime[],
  prices: Map<string, ReferencePriceType | NO_DATA>,
  estimateSettings: Settings | NO_DATA,
  vehicleDetail?: VehicleDetail,
  currentEstimate?: EstimateLocal,
): AddOperationFromEasyBasket => {
  const isCurrentEstimateWithSameVehicle =
    currentEstimate && currentEstimate?.vehicle?.vehicleKey === vehicleDetail?.vehicleKey;
  const estimateId = isCurrentEstimateWithSameVehicle ? currentEstimate.estimateId : uuidv4();
  const operationId = uuidv4();

  const estimateSettingsData = getData(estimateSettings);

  const mappedReferences: EasyBasketReference[] = references.map((part) => {
    const price = prices.get(part.reference);
    const priceData = getData(price);

    const quantity = part.quantity ? Number.parseInt(part.quantity, 10) : 1;

    return {
      referenceNumber: part.reference,
      catalogSource: 'DATAHUB',
      designation: part.designation ?? '',
      unitPrice: priceData?.clientView?.recommendedPriceVatExcluded?.toString() ?? '1',
      quantity,
      priceVatExcluded: priceData?.clientView?.recommendedPriceVatExcluded ?? '1',
      vatPercentage: priceData?.garageView?.vatPercentage ?? 20,
      garagePrice: priceData?.garageView?.vatExcludedPrice?.toString() ?? '1',
      discountRate: priceData?.garageView?.discountRate ?? '1',
      isCompatible: true,
      brand: part.brand,
    };
  });

  const mappedLaborTimes: EasyBasketLaborTime[] = estimateSettingsData
    ? laborTimes.map((lt) =>
        convertEasyBasketLaborTimeToEstimateLaborTimeWithQuantity(
          lt,
          Number(lt.characteristics?.quantity) || 1,
          estimateSettingsData,
        ),
      )
    : [];

  return {
    estimateId,
    operationId,
    operationName,
    vehicleDetail,
    references: mappedReferences,
    laborTimes: mappedLaborTimes,
  };
};
